import type { schemas } from '@gonfalon/openapi/types';

import type { MetricGroupKind, MetricKind } from './MetricKind';

type Metric = schemas['MetricRep'];
type MetricApiFilter = schemas['Filter']; // Represents the API version of Filter (unknown[] for values)
type CredibleInterval = schemas['CredibleIntervalRep'];
type RelativeDifference = schemas['RelativeDifferenceRep'];
type MetricGroupRep = schemas['MetricGroupRep'];
type MetricInGroupRep = schemas['MetricInGroupRep'];
type MetricListingRep = schemas['MetricListingRep'];
type MetricListingRepExpandableProperties = schemas['MetricListingRepExpandableProperties'];
type DependentMetric = schemas['MetricV2Rep'];
type EventActivityResponseItem = schemas['EventActivityResponseItemRep'];
type MetricDataSource = schemas['MetricDataSourceRep'];
type MetricGroupPost = schemas['MetricGroupPost'];

type DataSourceVariant = 'metric' | 'assignment' | 'attribute';

type AssignmentDataSourceColumnMappings = schemas['AssignmentDataSourceColumnMappingsRep'];

type AssignmentDataSource = schemas['AssignmentDataSourceRep'];

type AttributeDataSource = schemas['AttributeDataSourceRep'];

type DataSourceListItem = MetricDataSource | AssignmentDataSource | AttributeDataSource;

function isAssignmentDataSource(item: DataSourceListItem): item is AssignmentDataSource {
  return item.columnMappings !== undefined && 'experimentIdColumn' in item.columnMappings;
}

function isAttributeDataSource(item: DataSourceListItem): item is AttributeDataSource {
  return item.columnMappings !== undefined && 'selectedColumns' in item.columnMappings;
}

function isMetricDataSource(item: DataSourceListItem): item is MetricDataSource {
  return !isAssignmentDataSource(item) && !isAttributeDataSource(item);
}

type MetricEventUrlKind = 'canonical' | 'exact' | 'regex' | 'substring';

type MetricEventUrl =
  | {
      kind: 'canonical' | 'exact';
      url: string;
      pattern?: never;
      substring?: never;
    }
  | {
      kind: 'regex';
      pattern: string;
      url?: never;
      substring?: never;
    }
  | {
      kind: 'substring';
      substring: string;
      url?: never;
      pattern?: never;
    };

type FilterableMetricDashboardItem = {
  type: 'metric' | 'metric-group';
  key: string;
  name: string;
  description?: string;
  _creationDate: number;
  experimentCount?: number;
  guardedRolloutCount?: number;
  kind: MetricKind | MetricGroupKind;
  isNumeric?: boolean;
  unitAggregationType?: 'sum' | 'average';
  tags: string[];
  _attachedFlagCount?: number;
  metricGroupCount?: number;
  metricCount?: number;
};

export const METRIC_ANALYTICS_CHART_TYPES = {
  LINE: 'LINE' as const,
  BAR: 'BAR' as const,
};

type MeasureType = 'count' | 'occurrence' | 'value';

type GroupFilter = Omit<MetricApiFilter, 'attribute' | 'contextKind' | 'negate' | 'op' | 'type' | 'values'> & {
  negate: false;
  op: 'and' | 'or';
  type: 'group';
  values: MetricFilter[];
};

type ContextAttributeFilter = Omit<MetricApiFilter, 'type' | 'values'> & {
  type: 'contextAttribute';
  values: Array<string | number | boolean>;
};

type EventPropertyFilter = Omit<MetricApiFilter, 'type' | 'values'> & {
  type: 'eventProperty';
  values: Array<string | number | boolean>;
};

type MetricFilter = GroupFilter | ContextAttributeFilter | EventPropertyFilter;

type MetricsEventName =
  | 'Clicked CTA Create Metric Data Source'
  | 'Clicked Metric Data Source Badge Link'
  | 'Clone Metric Initiated'
  | 'Create Metric Group Error'
  | 'Create Metric Group Modal Closed'
  | 'Create Metric Group Modal Opened'
  | 'Create Metric Group Modal Submitted'
  | 'Create Metric Group Success'
  | 'Create Metric Modal Closed'
  | 'Create Metric Modal Opened'
  | 'Create Metric Modal Submitted'
  | 'Display Preferences Changed'
  | 'Edit Metric Modal Closed'
  | 'Edit Metric Modal Opened'
  | 'Edit Metric Modal Submitted'
  | 'Event Explorer Opened'
  | 'Event Explorer Search Updated'
  | 'Event Key Autofilled - Percentage'
  | 'Event Key Autofilled - Suggestion Rank'
  | 'Events Last Fetched Count'
  | 'Live Events Opened'
  | 'Metric Archive Error'
  | 'Metric Archive Initiated'
  | 'Metric Archive Success'
  | 'Metric Cloned Successfully'
  | 'Metric Created With Filters'
  | 'Metric Creation Error'
  | 'Metric Creation Success'
  | 'Metric Dashboard Viewed'
  | 'Metric Deletion Error'
  | 'Metric Deletion Success'
  | 'Metric Filters Updated'
  | 'Metric Group Creation Error'
  | 'Metric Group Creation Success'
  | 'Metric Group Deletion Error'
  | 'Metric Group Deletion Submitted'
  | 'Metric Group Deletion Success'
  | 'Metric Group Link Clicked'
  | 'Metric Group Update Error'
  | 'Metric Group Update Submitted'
  | 'Metric Group Update Success'
  | 'Metric Health Check Button Clicked'
  | 'Metric Health Check Documentation Link Clicked'
  | 'Metric Link Clicked'
  | 'Metric Restore Error'
  | 'Metric Restore Success'
  | 'Metric Sidebar Explore Event Clicked'
  | 'Metric Update Error'
  | 'Metric Update Success'
  | 'Metric Viewed'
  | 'Metrics Filter Button Clicked'
  | 'Metrics List Filtered'
  | 'Metrics List Search Updated'
  | 'Preview SQL Metric Data Source Error'
  | 'Preview SQL Metric Data Source Invalid Submission'
  | 'Preview SQL Metric Data Source Submitted'
  | 'Preview SQL Metric Data Source Success'
  | 'Sort Selected'
  | 'Tab clicked'
  | 'Tag Filter Applied'
  | 'Type Filter Selected'
  | 'Update Metric Group Error'
  | 'Update Metric Group Success'
  | 'Used In Filter Selected';

export {
  type Metric,
  type MetricApiFilter,
  type MetricFilter,
  type MetricEventUrlKind,
  type MetricEventUrl,
  type FilterableMetricDashboardItem,
  type MeasureType,
  type CredibleInterval,
  type RelativeDifference,
  type MetricGroupRep,
  type MetricInGroupRep,
  type MetricListingRep,
  type MetricListingRepExpandableProperties,
  type DependentMetric,
  type EventActivityResponseItem,
  type GroupFilter,
  type ContextAttributeFilter,
  type EventPropertyFilter,
  type MetricDataSource,
  type MetricGroupPost,
  type MetricsEventName,
  type DataSourceVariant,
  type AssignmentDataSource,
  type AssignmentDataSourceColumnMappings,
  type AttributeDataSource,
  type DataSourceListItem,
  isAssignmentDataSource,
  isAttributeDataSource,
  isMetricDataSource,
};
